import { useCallback, useEffect, useState } from "react";
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Space,
  Typography,
  Tabs,
  Row,
  Col,
  Image,
  message,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import BlogModal from "./BlogModal";
import CategoryModal from "./CategoryModal";
import blink from "../../assets/images/blink.png";
import { Blog, Category, CategoryFormValues } from "../../types/blog";
import { ListResponse, Pagination } from "../../types";
import blogService from "../../services/blogService";

const { Text } = Typography;

const BlogsManagement = () => {
  const [form] = Form.useForm();
  const [categoryForm] = Form.useForm();

  // Blog states
  const [isBlogModalVisible, setIsBlogModalVisible] = useState(false);
  const [isPreviewVisible, setIsPreviewVisible] = useState(false);
  const [isDeleteVisible, setIsDeleteVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState<Blog | null>(null);
  const [previewContent, setPreviewContent] = useState("");
  const [blogsData, setBlogsData] = useState<ListResponse<Blog>>();
  const [blogsPagination, setBlogsPagination] = useState<Pagination>({
    page: 1,
    resultsPerPage: 10,
  });
  const [isLoadingBlogs, setIsLoadingBlogs] = useState<boolean>(false);

  // Category states
  const [isCategoryModalVisible, setIsCategoryModalVisible] = useState(false);
  const [isCategoryDeleteVisible, setIsCategoryDeleteVisible] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [categoriesData, setCategoriesData] =
    useState<ListResponse<Category>>();
  const [categoriesPagination, setCategoriesPagination] = useState<Pagination>({
    page: 1,
    resultsPerPage: 10,
  });
  const [isLoadingCategories, setIsLoadingCategories] =
    useState<boolean>(false);

  const [isModalLoading, setIsModalLoading] = useState<boolean>(false);

  // Blog functions
  const getBlogs = useCallback(async () => {
    try {
      setIsLoadingBlogs(true);
      const response = await blogService.getBlogList(blogsPagination);
      setBlogsData(response);
    } catch (error) {
      message.error("Lỗi khi lấy danh sách bài đăng: " + error);
    } finally {
      setIsLoadingBlogs(false);
    }
  }, [blogsPagination]);

  useEffect(() => {
    getBlogs();
  }, [getBlogs]);

  const handleBlogsPaginationChange = (page: number, pageSize: number) => {
    setBlogsPagination({
      ...blogsPagination,
      page,
      resultsPerPage: pageSize,
    });
  };

  const handleBlogsSearch = (value: string) => {
    setBlogsPagination({
      ...blogsPagination,
      page: 1,
      search: value,
    });
  };

  const showBlogModal = async (record: Blog | null = null) => {
    setIsBlogModalVisible(true);
    setIsModalLoading(true);
    try {
      if (record) {
        const blog = await blogService.getBlogById(record.id);
        form.setFieldsValue({
          ...blog,
          blogCategoryId: blog.blogCategory.id,
        });
        setEditingRecord(blog);
      } else {
        form.resetFields();
        setEditingRecord(null);
      }
    } catch (error) {
      message.error("Lỗi khi lấy thông tin bài đăng: " + error);
    } finally {
      setIsModalLoading(false);
    }
  };

  const handleCancelBlogModal = () => {
    setIsBlogModalVisible(false);
    form.resetFields();
    setEditingRecord(null);
  };

  const handlePreview = async (record: Blog) => {
    try {
      const response = await blogService.getBlogById(record.id);
      setPreviewContent(response.content);
      setIsPreviewVisible(true);
    } catch (error) {
      message.error("Lỗi khi xem bài đăng: " + error);
    }
  };

  const handleDelete = (record: Blog) => {
    setEditingRecord(record);
    setIsDeleteVisible(true);
  };

  const confirmDelete = async () => {
    if (!editingRecord) return;
    try {
      await blogService.deleteBlog(editingRecord.id);
      message.success(`Xoá bài đăng ${editingRecord.title} thành công`);
      await getBlogs();
    } catch (error) {
      message.error("Lỗi khi xoá bài đăng: " + error);
    } finally {
      setEditingRecord(null);
      setIsDeleteVisible(false);
    }
  };

  const handleSubmit = async (values: any) => {
    try {
      if (editingRecord) {
        await blogService.updateBlog(editingRecord.id, values);
        message.success(`Cập nhật bài đăng ${editingRecord.title} thành công`);
      } else {
        await blogService.createBlog(values);
        message.success(`Thêm bài đăng ${values.title} thành công`);
      }
      handleCancelBlogModal();
      await getBlogs();
    } catch (error) {
      message.error("Lỗi khi thêm/cập nhật bài đăng: " + error);
    } finally {
      setIsModalLoading(false);
    }
  };

  // Category functions
  const getCategories = useCallback(async () => {
    try {
      setIsLoadingCategories(true);
      const response = await blogService.getCategories(categoriesPagination);
      setCategoriesData(response);
    } catch (error) {
      message.error("Lỗi khi lấy danh sách danh mục: " + error);
    } finally {
      setIsLoadingCategories(false);
    }
  }, [categoriesPagination]);

  useEffect(() => {
    getCategories();
  }, [getCategories]);

  const showCategoryModal = (record: Category | null = null) => {
    if (record) {
      categoryForm.setFieldsValue(record);
      setEditingCategory(record);
    } else {
      categoryForm.resetFields();
      setEditingCategory(null);
    }
    setIsCategoryModalVisible(true);
  };

  const handleCancelCategoryModal = () => {
    setIsCategoryModalVisible(false);
    categoryForm.resetFields();
    setEditingCategory(null);
  };

  const handleCategoryDelete = (record: Category) => {
    setEditingCategory(record);
    setIsCategoryDeleteVisible(true);
  };

  const confirmCategoryDelete = async () => {
    if (!editingCategory) return;
    try {
      await blogService.deleteCategory(editingCategory.id);
      message.success(`Xoá danh mục ${editingCategory.name} thành công`);
      await getCategories();
    } catch (error) {
      message.error("Lỗi khi xoá danh mục: " + error);
    } finally {
      setEditingCategory(null);
      setIsCategoryDeleteVisible(false);
    }
  };

  const handleCategorySubmit = async (values: CategoryFormValues) => {
    try {
      if (editingCategory) {
        await blogService.updateCategory(editingCategory.id, values);
        message.success(`Cập nhật danh mục ${editingCategory.name} thành công`);
      } else {
        await blogService.createCategory(values);
        message.success(`Thêm danh mục ${values.name} thành công`);
      }
      handleCancelCategoryModal();
      await getCategories();
    } catch (error) {
      message.error("Lỗi khi thêm/cập nhật danh mục: " + error);
    } finally {
      setEditingCategory(null);
      setIsCategoryModalVisible(false);
    }
  };

  const handleCategoriesPaginationChange = (page: number, pageSize: number) => {
    setCategoriesPagination({
      ...categoriesPagination,
      page,
      resultsPerPage: pageSize,
    });
  };

  const handleCategorySearch = (value: string) => {
    setCategoriesPagination({
      ...categoriesPagination,
      page: 1,
      search: value,
    });
  };
  const blogColumns = [
    {
      title: "Tiêu đề",
      dataIndex: "title",
      key: "title",
    },
    {
      title: "Danh mục",
      dataIndex: "blogCategory",
      key: "blogCategory",
      render: (blogCategory: Category) => blogCategory.name,
    },
    // {
    //   title: "Ngày đăng",
    //   dataIndex: "postedDate",
    //   key: "postedDate",
    // },
    {
      title: "Tình trạng",
      dataIndex: "published",
      key: "published",
      render: (published: boolean) => {
        if (published)
          return (
            <Text style={{ color: "#52c41a", fontWeight: 500 }}>Hiện</Text>
          );
        return <Text style={{ color: "#faad14", fontWeight: 500 }}>Ẩn</Text>;
      },
    },
    {
      title: "",
      key: "actions",
      render: (_: any, record: Blog) => (
        <Space>
          <Button
            icon={<EyeOutlined style={{ paddingLeft: "4px" }} />}
            onClick={() => handlePreview(record)}
          />
          <Button
            icon={<EditOutlined style={{ paddingLeft: "4px" }} />}
            onClick={() => showBlogModal(record)}
          />
          <Button
            icon={<DeleteOutlined style={{ paddingLeft: "4px" }} />}
            danger
            onClick={() => handleDelete(record)}
          />
        </Space>
      ),
    },
  ];

  const categoryColumns = [
    {
      title: "Danh mục",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Trạng thái",
      dataIndex: "isHidden",
      key: "isHidden",
      render: (isHidden: boolean) => {
        if (isHidden)
          return <Text style={{ color: "#faad14", fontWeight: 500 }}>Ẩn</Text>;
        return <Text style={{ color: "#52c41a", fontWeight: 500 }}>Hiện</Text>;
      },
    },
    {
      title: "",
      key: "actions",
      render: (_: any, record: Category) => (
        <Space>
          <Button
            icon={<EditOutlined style={{ paddingLeft: "4px" }} />}
            onClick={() => showCategoryModal(record)}
          />
          <Button
            icon={<DeleteOutlined style={{ paddingLeft: "4px" }} />}
            danger
            onClick={() => handleCategoryDelete(record)}
          />
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: "20px" }}>
      <h1 className="page-title">
        Quản lý bài đăng <Image src={blink} preview={false} className="blink-icon" />
      </h1>
      <Tabs defaultActiveKey="posts" type="card">
        <Tabs.TabPane tab="Bài đăng" key="posts">
          <Row
            style={{
              marginBottom: 16,
            }}
          >
            <Col
              xs={24}
              sm={15}
              md={12}
              lg={20}
              xl={20}
              style={{ marginTop: 10 }}
            >
              <Input.Search
                placeholder="Tìm kiếm"
                onChange={(e) => handleBlogsSearch(e.target.value)}
                style={{ maxWidth: 500 }}
                className="blog-search-input"
              />
            </Col>
            <Col
              xs={24}
              sm={9}
              md={12}
              lg={4}
              xl={4}
              style={{ textAlign: "right", marginTop: 10 }}
            >
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => showBlogModal()}
              >
                Thêm
              </Button>
            </Col>
          </Row>

          <Table
            rowKey="id"
            columns={blogColumns}
            dataSource={blogsData?.data || []}
            pagination={{
              current: blogsPagination.page,
              pageSize: blogsPagination.resultsPerPage,
              total: blogsData?.totalResults || 0,
              onChange: handleBlogsPaginationChange,
            }}
            loading={isLoadingBlogs}
            scroll={{ x: "max-content" }}
          />
        </Tabs.TabPane>

        <Tabs.TabPane tab="Danh mục" key="categories">
          <Row
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 16,
            }}
          >
            <Col
              xs={24}
              sm={15}
              md={12}
              lg={20}
              xl={20}
              style={{ marginTop: 10 }}
            >
              <Input.Search
                placeholder="Tìm kiếm"
                onChange={(e) => handleCategorySearch(e.target.value)}
                style={{ maxWidth: 500 }}
                className="blog-search-input"
              />
            </Col>
            <Col
              xs={24}
              sm={9}
              md={12}
              lg={4}
              xl={4}
              style={{ textAlign: "right", marginTop: 10 }}
            >
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => showCategoryModal()}
              >
                Thêm
              </Button>
            </Col>
          </Row>

          <Table
            rowKey="id"
            columns={categoryColumns}
            dataSource={categoriesData ? categoriesData.data : []}
            pagination={{
              current: categoriesPagination.page,
              pageSize: categoriesPagination.resultsPerPage,
              total: categoriesData ? categoriesData.totalResults : 0,
              onChange: handleCategoriesPaginationChange,
            }}
            loading={isLoadingCategories}
            scroll={{ x: "max-content" }}
          />
        </Tabs.TabPane>
      </Tabs>

      {/* Blog Modal */}
      <BlogModal
        visible={isBlogModalVisible}
        form={form}
        onFinish={handleSubmit}
        onCancel={handleCancelBlogModal}
        editingRecord={editingRecord}
        categories={categoriesData?.data || []}
        isModalLoading={isModalLoading}
        setIsModalLoading={setIsModalLoading}
      />

      {/* Category Modal */}
      <CategoryModal
        visible={isCategoryModalVisible}
        form={categoryForm}
        onFinish={handleCategorySubmit}
        onCancel={handleCancelCategoryModal}
        editingRecord={editingCategory}
      />

      {/* Preview Modal */}
      <Modal
        title="Blog Post Preview"
        open={isPreviewVisible}
        onCancel={() => setIsPreviewVisible(false)}
        footer={null}
        width={800}
      >
        <div
          className="preview-content"
          dangerouslySetInnerHTML={{ __html: previewContent }}
        />
      </Modal>

      {/* Blog Delete Modal */}
      <Modal
        title="Xoá bài đăng"
        open={isDeleteVisible}
        onOk={confirmDelete}
        onCancel={() => setIsDeleteVisible(false)}
        okText="Xoá"
        okButtonProps={{ danger: true }}
        cancelText="Hủy"
      >
        Bạn có chắc chắn muốn xoá bài đăng {editingRecord?.title} không?
      </Modal>

      {/* Category Delete Modal */}
      <Modal
        title="Xoá danh mục"
        open={isCategoryDeleteVisible}
        onOk={confirmCategoryDelete}
        onCancel={() => setIsCategoryDeleteVisible(false)}
        okText="Xoá"
        okButtonProps={{ danger: true }}
        cancelText="Huỷ"
      >
        Bạn có chắc chắn muốn xoá danh mục {editingCategory?.name} không?
      </Modal>
    </div>
  );
};

export default BlogsManagement;
